package one.healthium.notifications.manager

import kotlinx.browser.window
import one.healthium.Platform

/**
 * JS/Web implementation of FileSaver.
 * Since FileSystem.SYSTEM is not supported in JS target, we convert bytes to data URLs
 * that can be used directly in web contexts (e.g., for notifications, images, etc.).
 *
 * For persistent storage, we could use IndexedDB, but for notification images,
 * data URLs are sufficient and simpler.
 */
actual class FileSaver actual constructor(
    private val platform: Platform
) {
    // In-memory storage for JS platform - maps fileName to data URL
    private val storage = mutableMapOf<String, String>()

    /**
     * Converts bytes to a data URL and stores it in memory.
     * Returns a data URL that can be used directly in web contexts.
     *
     * For JS/Web, the returned "path" is actually a data URL (data:image/png;base64,...)
     * which can be used as-is for notification icons and other purposes.
     */
    actual fun save(fileName: String, bytes: ByteArray): String {
        // Convert ByteArray to base64 data URL
        val base64 = bytesToBase64(bytes)

        // Determine MIME type from file extension
        val mimeType = when {
            fileName.endsWith(".png", ignoreCase = true) -> "image/png"
            fileName.endsWith(".jpg", ignoreCase = true) ||
            fileName.endsWith(".jpeg", ignoreCase = true) -> "image/jpeg"
            fileName.endsWith(".gif", ignoreCase = true) -> "image/gif"
            fileName.endsWith(".webp", ignoreCase = true) -> "image/webp"
            fileName.endsWith(".svg", ignoreCase = true) -> "image/svg+xml"
            else -> "application/octet-stream"
        }

        val dataUrl = "data:$mimeType;base64,$base64"

        // Store in memory for potential later retrieval
        storage[fileName] = dataUrl

        return dataUrl
    }

    /**
     * Convert ByteArray to base64 string.
     * Uses the browser's btoa function for base64 encoding.
     */
    private fun bytesToBase64(bytes: ByteArray): String {
        // Convert ByteArray to binary string directly
        val binaryString = buildString {
            for (byte in bytes) {
                // Convert each byte to unsigned char (0-255)
                val unsignedByte = byte.toInt() and 0xFF
                append(unsignedByte.toChar())
            }
        }

        // Use browser's btoa to encode to base64
        return window.btoa(binaryString)
    }
}

